/**
 * BackgroundAmbiance - Subtle floating dust motes for atmosphere
 *
 * Creates a gentle ambient atmosphere with:
 * - Very small, faint dust particles that drift slowly
 * - Clearly decorative - not food-like at all
 *
 * Designed to add subtle life to the world without being mistaken for collectibles.
 */

import { Container, Graphics } from 'pixi.js';
import { WORLD_RADIUS } from '@wooorms/shared';
import { AMBIANCE_DUST_COUNT, AMBIANCE_DUST_SPEED } from '../../constants';
import { EyeProtectionSettings } from '../../ui/EyeProtectionToggle';

/**
 * Dust mote data - tiny floating specks
 */
interface DustMote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  alpha: number;
  size: number;
}

/**
 * BackgroundAmbiance - Adds subtle atmosphere to the game world
 *
 * Uses a single batched Graphics object for all dust motes.
 * Particles are intentionally tiny and faint to avoid confusion with food.
 */
export class BackgroundAmbiance {
  /** Main container */
  public container: Container;

  /** Batched Graphics for dust motes */
  private dustGraphics: Graphics;

  /** Dust motes data */
  private dust: DustMote[] = [];

  /** World bounds */
  private worldRadius: number;

  constructor(worldRadius: number = WORLD_RADIUS) {
    this.worldRadius = worldRadius;
    this.container = new Container();
    this.container.label = 'background-ambiance';

    // Single Graphics object for all dust
    this.dustGraphics = new Graphics();
    this.container.addChild(this.dustGraphics);

    this.initializeElements();
  }

  /**
   * Initialize dust motes
   */
  private initializeElements(): void {
    // Skip in strong eye protection mode
    if (EyeProtectionSettings.getInstance().isStrong()) {
      return;
    }

    // Reduce count for mild eye protection
    const factor = EyeProtectionSettings.getInstance().isMildOrHigher() ? 0.5 : 1.0;
    this.createDust(Math.floor(AMBIANCE_DUST_COUNT * factor));
  }

  /**
   * Create floating dust motes - tiny, faint particles
   */
  private createDust(count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * this.worldRadius * 0.9;
      const moveAngle = Math.random() * Math.PI * 2;

      // Very small size (0.5 - 1.5 pixels) and low alpha (0.08 - 0.18)
      // Much smaller and fainter than food to avoid confusion
      const mote: DustMote = {
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
        vx: Math.cos(moveAngle) * AMBIANCE_DUST_SPEED * 0.5,
        vy: Math.sin(moveAngle) * AMBIANCE_DUST_SPEED * 0.5,
        alpha: 0.08 + Math.random() * 0.1,
        size: 0.5 + Math.random() * 1.0,
      };

      this.dust.push(mote);
    }
  }

  /**
   * Draw a dust mote - simple tiny dot
   */
  private drawDust(g: Graphics, mote: DustMote): void {
    // Simple tiny white/gray dot - clearly not food
    g.circle(mote.x, mote.y, mote.size);
    g.fill({ color: 0xaaaaaa, alpha: mote.alpha });
  }

  /**
   * Wrap dust mote within world bounds
   */
  private wrapDust(mote: DustMote): void {
    const dist = Math.sqrt(mote.x * mote.x + mote.y * mote.y);
    if (dist > this.worldRadius * 0.95) {
      // Respawn on opposite side
      const angle = Math.atan2(mote.y, mote.x) + Math.PI;
      const newDist = this.worldRadius * 0.5;
      mote.x = Math.cos(angle) * newDist;
      mote.y = Math.sin(angle) * newDist;
    }
  }

  /**
   * Update all dust motes
   */
  update(dt: number): void {
    // Skip in strong eye protection mode
    if (EyeProtectionSettings.getInstance().isStrong()) {
      return;
    }

    // Clear and redraw
    this.dustGraphics.clear();

    for (const mote of this.dust) {
      // Slow drift
      mote.x += mote.vx * dt;
      mote.y += mote.vy * dt;

      // Very gentle random drift
      mote.vx += (Math.random() - 0.5) * 1.0 * dt;
      mote.vy += (Math.random() - 0.5) * 1.0 * dt;

      // Clamp velocity (very slow)
      const maxSpeed = AMBIANCE_DUST_SPEED * 0.8;
      const speed = Math.sqrt(mote.vx * mote.vx + mote.vy * mote.vy);
      if (speed > maxSpeed) {
        mote.vx = (mote.vx / speed) * maxSpeed;
        mote.vy = (mote.vy / speed) * maxSpeed;
      }

      this.wrapDust(mote);
      this.drawDust(this.dustGraphics, mote);
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.dust = [];
    this.dustGraphics.destroy();
    this.container.destroy({ children: true });
  }
}
